import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Activity, AlertCircle, Clock, ExternalLink } from "lucide-react";
import JsonViewer from "@/components/JsonViewer";
import { getBackendUrl as resolveBackendUrl } from "@/lib/runtimeConfig";

const STATUS_TONE = {
    completed: "text-emerald-300",
    failed: "text-red-300",
    running: "text-amber-300",
    awaiting_approval: "text-amber-200",
    cancelled: "text-white/40",
};

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function SharedTracePage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeNode, setActiveNode] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const base = resolveBackendUrl();
                const res = await axios.get(`${base}/api/v1/observability/trace/${token}`);
                setData(res.data);
            } catch (e) {
                setError({
                    status: e?.response?.status,
                    message: formatApiErrorDetail(e?.response?.data?.detail) || e.message,
                });
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [token]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] text-white/60 flex items-center justify-center font-mono text-xs">
                Loading shared trace…
            </div>
        );
    }

    if (error) {
        const expired = error.status === 410;
        const invalid = error.status === 401;
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="shared-trace-error">
                <div className="max-w-md w-full bg-[#0a0a0a] border border-white/10 rounded-md p-8 text-center">
                    <AlertCircle className="w-10 h-10 mx-auto mb-4 text-red-400" strokeWidth={1.3} />
                    <div className="font-display text-xl tracking-tight mb-2">
                        {expired ? "This link has expired" : invalid ? "Invalid share link" : "Unable to load trace"}
                    </div>
                    <div className="text-sm text-white/60 mb-5">{error.message}</div>
                    <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#7C3AED] hover:text-[#a17bff]">
                        Sign in to the console <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </div>
        );
    }

    const isFlow = data?.kind === "flow_run";
    const fr = data?.flow_run;
    const ar = data?.agent_run;
    const share = data?.share;

    return (
        <div className="min-h-screen bg-[#050505] text-white" data-testid="shared-trace-page">
            {/* Top ribbon */}
            <header className="border-b border-white/10 bg-[#0a0a0a]">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
                    <div className="flex items-center gap-3">
                        <div className="grid grid-cols-1 gap-1">
                            <div className="flex gap-0.5">
                                <span className="w-1.5 h-1.5 rounded-sm bg-[#7C3AED]" />
                                <span className="w-1.5 h-1.5 rounded-sm bg-[#7C3AED]" />
                            </div>
                            <div className="flex gap-0.5">
                                <span className="w-1.5 h-1.5 rounded-sm bg-[#7C3AED]" />
                                <span className="w-1.5 h-1.5 rounded-sm bg-[#7C3AED]" />
                            </div>
                        </div>
                        <div>
                            <div className="font-display text-sm font-semibold tracking-tight">
                                <span className="text-white">Site</span><span className="text-[#7C3AED]">Centric</span>
                                <span className="text-white/60 ml-1.5 text-[11px] font-mono uppercase tracking-[0.18em]">AI</span>
                            </div>
                            <div className="label-xs mt-0.5 text-white/40">Shared execution trace · read-only</div>
                        </div>
                    </div>
                    <div className="ml-auto flex items-center gap-3 text-[11px] font-mono text-white/60">
                        <span className="flex items-center gap-1.5">
                            <Clock className="w-3 h-3" strokeWidth={2} />
                            Expires {new Date(share?.expires_at).toLocaleString()}
                        </span>
                        <Link to="/login" className="text-[#7C3AED] hover:text-[#a17bff] flex items-center gap-1">
                            Admin sign-in <ExternalLink className="w-3 h-3" />
                        </Link>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-8">
                {/* Banner */}
                <div className="mb-6 p-4 border border-[#7C3AED]/30 bg-[#7C3AED]/5 rounded-md flex items-start gap-3">
                    <Activity className="w-4 h-4 mt-0.5 text-[#7C3AED]" strokeWidth={1.8} />
                    <div className="text-xs text-white/75 leading-relaxed">
                        <div className="text-white font-medium mb-0.5">
                            {isFlow ? `Flow run · ${fr?.flow_slug}` : `Agent run · ${ar?.agent_slug}`}
                        </div>
                        <div className="text-white/55 font-mono text-[11px]">
                            request_id {share?.request_id} · shared on {new Date(share?.issued_at).toLocaleString()}
                        </div>
                    </div>
                </div>

                {/* Flow run view */}
                {isFlow && (
                    <div className="grid grid-cols-3 gap-0 bg-[#0a0a0a] border border-white/10 rounded-md overflow-hidden">
                        <div className="col-span-1 border-r border-white/10 p-5 space-y-3 max-h-[75vh] overflow-auto">
                            <div>
                                <div className="label-xs text-white/40">Flow</div>
                                <div className="font-display text-base">{fr?.flow_slug} v{fr?.version_no}</div>
                                <div className="text-xs text-white/60 font-mono mt-1">
                                    <span className={STATUS_TONE[fr?.status] || ""}>{fr?.status}</span>
                                    {" · "}env={fr?.environment || "—"}
                                    {" · "}cost=${(fr?.totals?.cost_usd || 0).toFixed(5)}
                                    {" · "}latency={fr?.totals?.latency_ms || 0}ms
                                </div>
                            </div>
                            <div className="border-t border-white/10 pt-3 space-y-1.5">
                                <div className="label-xs text-white/40 mb-1">Nodes ({data.node_executions?.length || 0})</div>
                                {(data.node_executions || []).map((ne) => (
                                    <button
                                        key={ne.id}
                                        onClick={() => setActiveNode(ne)}
                                        data-testid={`shared-node-${ne.node_id}`}
                                        className={`w-full text-left p-2.5 border rounded-md transition-colors ${activeNode?.id === ne.id ? "border-[#7C3AED]/60 bg-[#7C3AED]/10" : "border-white/10 hover:bg-white/5"}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-mono text-white/85">{ne.node_id}</span>
                                            <span className={`text-[10px] uppercase tracking-wider font-mono ${STATUS_TONE[ne.status] || "text-white/40"}`}>{ne.status}</span>
                                        </div>
                                        <div className="text-[11px] text-white/40 mt-1 font-mono">
                                            {ne.node_type} · {ne.latency_ms || 0}ms · ${(ne.cost_usd || 0).toFixed(5)}
                                        </div>
                                        {ne.agent_run_id && (
                                            <div className="text-[10px] text-emerald-300 font-mono mt-0.5">
                                                → agent_run {ne.agent_run_id.slice(0, 8)}
                                            </div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="col-span-2 p-5 space-y-3 max-h-[75vh] overflow-auto">
                            {!activeNode ? (
                                <>
                                    <div className="label-xs text-white/40">Run input</div>
                                    <JsonViewer data={fr?.input_payload || {}} maxHeight={220} testId="shared-trace-input" />
                                    <div className="label-xs text-white/40 mt-3">Run output</div>
                                    <JsonViewer data={fr?.output_payload || {}} maxHeight={220} testId="shared-trace-output" />
                                </>
                            ) : (
                                <div className="space-y-3" data-testid="shared-node-detail">
                                    <div>
                                        <div className="label-xs text-white/40">Node</div>
                                        <div className="font-display text-base">{activeNode.node_id}{" "}
                                            <span className="text-white/40 text-sm font-mono">({activeNode.node_type})</span>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="label-xs text-white/40 mb-1">Input</div>
                                        <JsonViewer data={activeNode.input_payload || {}} maxHeight={160} />
                                    </div>
                                    <div>
                                        <div className="label-xs text-white/40 mb-1">Output</div>
                                        <JsonViewer data={activeNode.output_payload || {}} maxHeight={160} />
                                    </div>
                                    {activeNode.error && (
                                        <div className="text-xs font-mono text-red-300 border border-red-300/30 bg-red-300/10 rounded p-2 whitespace-pre-wrap">
                                            {activeNode.error}
                                        </div>
                                    )}
                                    {activeNode.agent_run && (
                                        <div className="border-t border-white/10 pt-3">
                                            <div className="label-xs text-emerald-300 mb-1">Linked agent run</div>
                                            <div className="text-xs font-mono text-white/70 mb-2">
                                                {activeNode.agent_run.agent_slug} v{activeNode.agent_run.version_no}
                                                {" · "}{activeNode.agent_run.status}
                                            </div>
                                            <div className="label-xs text-white/40 mb-1">Rendered system prompt</div>
                                            <pre className="text-[11px] font-mono whitespace-pre-wrap bg-[#050505] border border-white/10 rounded p-2 max-h-40 overflow-auto">
                                                {activeNode.agent_run.rendered_system_prompt || "(no system prompt)"}
                                            </pre>
                                            <div className="label-xs text-white/40 mb-1 mt-2">Rendered user prompt</div>
                                            <pre className="text-[11px] font-mono whitespace-pre-wrap bg-[#050505] border border-white/10 rounded p-2 max-h-40 overflow-auto">
                                                {activeNode.agent_run.rendered_user_prompt || "(no user prompt)"}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Agent run view */}
                {!isFlow && ar && (
                    <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-5 space-y-3">
                        <div>
                            <div className="label-xs text-white/40">Agent run</div>
                            <div className="font-display text-base">{ar.agent_slug} v{ar.version_no}</div>
                            <div className="text-xs text-white/60 font-mono mt-1">
                                <span className={STATUS_TONE[ar.status] || ""}>{ar.status}</span>
                                {" · "}cost=${(ar.total_cost_usd || 0).toFixed(5)}
                                {" · "}latency={ar.latency_ms || 0}ms
                            </div>
                        </div>
                        <div>
                            <div className="label-xs text-white/40 mb-1">Input</div>
                            <JsonViewer data={ar.input_payload || {}} maxHeight={180} />
                        </div>
                        <div>
                            <div className="label-xs text-white/40 mb-1">Output</div>
                            <JsonViewer data={ar.output_payload || {}} maxHeight={180} />
                        </div>
                        {ar.error && (
                            <div className="text-xs font-mono text-red-300 border border-red-300/30 bg-red-300/10 rounded p-2 whitespace-pre-wrap">
                                {ar.error}
                            </div>
                        )}
                        <div className="label-xs text-white/40 mb-1">Rendered system prompt</div>
                        <pre className="text-[11px] font-mono whitespace-pre-wrap bg-[#050505] border border-white/10 rounded p-2 max-h-40 overflow-auto">
                            {ar.rendered_system_prompt || "(no system prompt)"}
                        </pre>
                        <div className="label-xs text-white/40 mb-1">Rendered user prompt</div>
                        <pre className="text-[11px] font-mono whitespace-pre-wrap bg-[#050505] border border-white/10 rounded p-2 max-h-40 overflow-auto">
                            {ar.rendered_user_prompt || "(no user prompt)"}
                        </pre>
                    </div>
                )}
            </main>
        </div>
    );
}
